// Reader for splat-transform collision voxels (.voxel.json + .voxel.bin, format 1.x).
// Coordinates are PlayCanvas engine world space in metres (Y up). No engine dependency, so the
// pipeline's Node validator uses the exact same code as the viewer.

const SOLID_LEAF = 0xff000000;
const DENSE_LIMIT = 96 * 1024 * 1024; // voxels; above this, queries walk the octree instead

function popcount(n) {
    n = n - ((n >>> 1) & 0x55555555);
    n = (n & 0x33333333) + ((n >>> 2) & 0x33333333);
    return (((n + (n >>> 4)) & 0x0f0f0f0f) * 0x01010101) >>> 24;
}

export class VoxelGrid {
    /**
     * @param {object} header - parsed .voxel.json
     * @param {ArrayBuffer} buffer - contents of .voxel.bin
     */
    constructor(header, buffer) {
        const major = parseInt(String(header.version).split('.')[0], 10);
        if (major !== 1) throw new Error(`Unsupported voxel format ${header.version}`);
        if (header.leafSize !== 4) throw new Error(`Unsupported leaf size ${header.leafSize}`);

        this.res = header.voxelResolution;
        this.min = header.gridBounds.min.slice();
        this.max = header.gridBounds.max.slice();
        this.depth = header.treeDepth;
        this.nx = Math.round((this.max[0] - this.min[0]) / this.res);
        this.ny = Math.round((this.max[1] - this.min[1]) / this.res);
        this.nz = Math.round((this.max[2] - this.min[2]) / this.res);
        this.nodes = new Uint32Array(buffer, 0, header.nodeCount);
        this.leaf = new Uint32Array(buffer, header.nodeCount * 4, header.leafDataCount);

        // Out-of-grid space counts as solid: the viewer must never walk off the scanned volume
        this.outsideSolid = true;
        this.dense = null;
        if (this.nx * this.ny * this.nz <= DENSE_LIMIT) this._decodeDense();
    }

    static async load(jsonUrl, fetchFn = fetch) {
        const header = await (await fetchFn(jsonUrl)).json();
        const binUrl = jsonUrl.replace(/\.voxel\.json(\?.*)?$/, '.voxel.bin$1');
        const res = await fetchFn(binUrl);
        if (!res.ok) throw new Error(`voxel bin ${res.status}`);
        return new VoxelGrid(header, await res.arrayBuffer());
    }

    /**
     * Stand-in when a tour has no collision data (or it failed to load): a flat floor at y=0 inside
     * the floor-plan bounds, walled in by the grid edge. Walking stays the default; only walls and
     * furniture are missing.
     * @param {{min:number[], max:number[]}} bounds - x/z extent in metres
     * @param {number} [res]
     * @param {{res:number, min:number[], nx:number, nz:number, cells:Uint8Array}|null} [walk] - walk mask of the
     *   build (collision/walk.json, tools/walk_fallback.py): cells without floor or with furniture/walls in the body
     *   band are 0 and become solid columns, so the visitor stays in the rooms instead of walking through walls
     */
    static flatFloor(bounds, res = 0.05, walk = null) {
        const g = Object.create(VoxelGrid.prototype);
        g.res = res;
        g.min = [bounds.min[0], -0.2, bounds.min[2]];
        g.max = [bounds.max[0], 3.0, bounds.max[2]];
        g.depth = 0;
        g.nx = Math.max(1, Math.round((g.max[0] - g.min[0]) / res));
        g.ny = Math.round((g.max[1] - g.min[1]) / res);
        g.nz = Math.max(1, Math.round((g.max[2] - g.min[2]) / res));
        g.nodes = new Uint32Array(0);
        g.leaf = new Uint32Array(0);
        g.outsideSolid = true;
        g.dense = new Uint32Array(Math.ceil((g.nx * g.ny * g.nz) / 32));
        const floorRows = Math.round(0.2 / res);
        for (let iz = 0; iz < g.nz; iz++) {
            for (let iy = 0; iy < floorRows; iy++) {
                for (let ix = 0; ix < g.nx; ix++) {
                    const i = g._index(ix, iy, iz);
                    g.dense[i >>> 5] |= 1 << (i & 31);
                }
            }
        }
        if (walk) {
            for (let iz = 0; iz < g.nz; iz++) {
                const wz = Math.floor((g.min[2] + (iz + 0.5) * res - walk.min[1]) / walk.res);
                for (let ix = 0; ix < g.nx; ix++) {
                    const wx = Math.floor((g.min[0] + (ix + 0.5) * res - walk.min[0]) / walk.res);
                    const free = wx >= 0 && wz >= 0 && wx < walk.nx && wz < walk.nz && walk.cells[wz * walk.nx + wx] === 1;
                    if (free) continue;
                    for (let iy = floorRows; iy < g.ny; iy++) {
                        const i = g._index(ix, iy, iz);
                        g.dense[i >>> 5] |= 1 << (i & 31);
                    }
                }
            }
        }
        g.flat = true;
        return g;
    }

    /**
     * Walk mask of the build (collision/walk.json + walk.bin): one byte per x/z cell, 1 = walkable.
     * @param {string} jsonUrl
     */
    static async loadWalkMask(jsonUrl, fetchFn = fetch) {
        const h = await (await fetchFn(jsonUrl)).json();
        if (h.version !== 1) throw new Error(`Unsupported walk mask ${h.version}`);
        const res = await fetchFn(new URL(h.file, new URL(jsonUrl, location.href)).href);
        if (!res.ok) throw new Error(`walk mask ${res.status}`);
        const cells = new Uint8Array(await res.arrayBuffer());
        if (cells.length !== h.nx * h.nz) throw new Error(`walk mask: ${cells.length} cells, expected ${h.nx * h.nz}`);
        return { res: h.res, min: h.min, nx: h.nx, nz: h.nz, cells };
    }

    _index(ix, iy, iz) {
        return (iz * this.ny + iy) * this.nx + ix;
    }

    _decodeDense() {
        const bits = new Uint32Array(Math.ceil((this.nx * this.ny * this.nz) / 32));
        this.dense = bits;
        if (this.nodes.length === 0) return;
        const set = (ix, iy, iz) => {
            if (ix >= this.nx || iy >= this.ny || iz >= this.nz) return;
            const i = this._index(ix, iy, iz);
            bits[i >>> 5] |= 1 << (i & 31);
        };
        const fillBlock = (vx, vy, vz, size) => {
            const ex = Math.min(vx + size, this.nx), ey = Math.min(vy + size, this.ny), ez = Math.min(vz + size, this.nz);
            for (let z = vz; z < ez; z++) for (let y = vy; y < ey; y++) for (let x = vx; x < ex; x++) set(x, y, z);
        };
        // Iterative walk: (node index, depth, block origin in blocks)
        const stack = [[0, 0, 0, 0, 0]];
        while (stack.length) {
            const [ni, d, bx, by, bz] = stack.pop();
            const word = this.nodes[ni];
            const blocks = 1 << (this.depth - d);
            if (word === SOLID_LEAF) {
                fillBlock(bx * 4, by * 4, bz * 4, blocks * 4);
            } else if (word >>> 24 === 0) {
                const li = word & 0xffffff;
                const lo = this.leaf[2 * li], hi = this.leaf[2 * li + 1];
                for (let bit = 0; bit < 64; bit++) {
                    const on = bit < 32 ? (lo >>> bit) & 1 : (hi >>> (bit - 32)) & 1;
                    if (on) set(bx * 4 + (bit & 3), by * 4 + ((bit >> 2) & 3), bz * 4 + ((bit >> 4) & 3));
                }
            } else {
                const mask = word >>> 24;
                const first = word & 0xffffff;
                const half = blocks >> 1;
                let k = 0;
                for (let oct = 0; oct < 8; oct++) {
                    if (!(mask & (1 << oct))) continue;
                    stack.push([first + k, d + 1, bx + (oct & 1) * half, by + ((oct >> 1) & 1) * half, bz + ((oct >> 2) & 1) * half]);
                    k++;
                }
            }
        }
    }

    _treeSolid(ix, iy, iz) {
        if (this.nodes.length === 0) return false;
        const bx = ix >> 2, by = iy >> 2, bz = iz >> 2;
        let node = this.nodes[0];
        for (let d = 0; d < this.depth; d++) {
            if (node === SOLID_LEAF) return true;
            const mask = node >>> 24;
            const s = this.depth - 1 - d;
            const oct = ((bx >> s) & 1) | (((by >> s) & 1) << 1) | (((bz >> s) & 1) << 2);
            if (!(mask & (1 << oct))) return false;
            node = this.nodes[(node & 0xffffff) + popcount(mask & ((1 << oct) - 1))];
        }
        if (node === SOLID_LEAF) return true;
        const li = node & 0xffffff;
        const bit = (ix & 3) + ((iy & 3) << 2) + ((iz & 3) << 4);
        const word = bit < 32 ? this.leaf[2 * li] : this.leaf[2 * li + 1];
        return ((word >>> (bit & 31)) & 1) === 1;
    }

    /** Solid test by voxel index. */
    solid(ix, iy, iz) {
        if (ix < 0 || iy < 0 || iz < 0 || ix >= this.nx || iy >= this.ny || iz >= this.nz) return this.outsideSolid;
        if (this.dense) {
            const i = this._index(ix, iy, iz);
            return ((this.dense[i >>> 5] >>> (i & 31)) & 1) === 1;
        }
        return this._treeSolid(ix, iy, iz);
    }

    ix(x) { return Math.floor((x - this.min[0]) / this.res); }
    iy(y) { return Math.floor((y - this.min[1]) / this.res); }
    iz(z) { return Math.floor((z - this.min[2]) / this.res); }

    solidAt(x, y, z) {
        return this.solid(this.ix(x), this.iy(y), this.iz(z));
    }

    /** World Y of the top face of voxel row iy. */
    top(iy) {
        return this.min[1] + (iy + 1) * this.res;
    }

    /**
     * March a ray through the grid (Amanatides & Woo). Returns the first solid hit or null.
     * @returns {{t:number, point:number[], normal:number[], voxel:number[]}|null}
     */
    raycast(origin, dir, maxDist = 50) {
        const res = this.res;
        let x = this.ix(origin[0]), y = this.iy(origin[1]), z = this.iz(origin[2]);
        const step = dir.map(d => (d > 0 ? 1 : d < 0 ? -1 : 0));
        const next = (i, a) => this.min[a] + (i + (step[a] > 0 ? 1 : 0)) * res;
        const tMax = [0, 1, 2].map(a => (step[a] === 0 ? Infinity : (next([x, y, z][a], a) - origin[a]) / dir[a]));
        const tDelta = dir.map(d => (d === 0 ? Infinity : res / Math.abs(d)));
        const normal = [0, 0, 0];
        let t = 0;
        // Starting inside solid (e.g. camera clipped into a wall): report nothing rather than a hit at 0
        if (this.solid(x, y, z)) return null;
        while (t <= maxDist) {
            let a = tMax[0] < tMax[1] ? (tMax[0] < tMax[2] ? 0 : 2) : (tMax[1] < tMax[2] ? 1 : 2);
            t = tMax[a];
            if (a === 0) x += step[0]; else if (a === 1) y += step[1]; else z += step[2];
            tMax[a] += tDelta[a];
            if (this.solid(x, y, z)) {
                normal[0] = normal[1] = normal[2] = 0;
                normal[a] = -step[a];
                return {
                    t,
                    point: [origin[0] + dir[0] * t, origin[1] + dir[1] * t, origin[2] + dir[2] * t],
                    normal: normal.slice(),
                    voxel: [x, y, z]
                };
            }
        }
        return null;
    }
}
